import "@/styles/index.css";
import "@/i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createQueryClient, setUnauthenticatedHandler } from "@/app/query-client";
import { sessionKeys } from "@/lib/auth/session";
import { routeTree } from "./routeTree.gen";

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Query owns freshness; the router re-runs loaders whenever asked.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A 401 from anywhere means the session has ended. Forget it and let the
// _authenticated guard send the reader to sign in.
setUnauthenticatedHandler(() => {
  queryClient.setQueryData(sessionKeys.current(), null);
  void router.invalidate();
});

async function enableMocking(): Promise<void> {
  // __MOCK_BUILD__ is replaced by a literal at build time (vite.config.ts), so a
  // live build contains no mock worker and no fixtures.
  if (!__MOCK_BUILD__) return;
  const { worker } = await import("@/api/msw/browser");
  await worker.start({ onUnhandledRequest: "bypass", quiet: true });
}

void enableMocking().then(() => {
  const root = document.getElementById("root");
  if (!root) throw new Error("index.html has no #root element");
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
